import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { CreditPackage } from '@/hooks/useCredits';

export const CreditPurchase = () => {
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchCreditPackages();
  }, []);

  const fetchCreditPackages = async () => {
    try {
      const { data, error } = await supabase
        .from('credit_packages')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      setPackages(data || []);
    } catch (error) {
      console.error('Error fetching credit packages:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les packs de crédits.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (priceInCents: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(priceInCents / 100);
  };

  const handlePurchase = async (packageData: CreditPackage) => {
    setPurchasing(packageData.id);

    try {
      toast({
        title: "Redirection vers le paiement",
        description: "Une nouvelle page va s'ouvrir pour finaliser votre achat.",
      });

      // Only send packageId — price & credits are looked up server-side for security
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: {
          type: 'credits',
          packageId: packageData.id,
        }
      });

      if (error) throw error;

      if (data?.url) {
        const newWindow = window.open(data.url, '_blank');

        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          window.location.href = data.url;
        }
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      toast({
        title: "Erreur de paiement",
        description: "Impossible de créer la session de paiement. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {packages.map((pkg) => (
        <Card key={pkg.id} className="transition-all duration-200 hover:shadow-md hover:border-primary/50">
          <CardContent className="flex flex-col items-center gap-3 p-5">
            <div className="text-3xl font-bold">
              {pkg.credits} <span className="text-lg font-medium text-muted-foreground">crédits</span>
            </div>
            <div className="text-xl font-semibold text-primary">
              {formatPrice(pkg.price_cents)}
            </div>
            <Button
              className="w-full group"
              onClick={() => handlePurchase(pkg)}
              disabled={purchasing === pkg.id}
              size="sm"
            >
              {purchasing === pkg.id ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Redirection...
                </>
              ) : (
                <>
                  Acheter
                  <ExternalLink className="h-3.5 w-3.5 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
